import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handoffAiJobToBackground } from '@/components/ai-job/aiJobHandoff';
import type { AiJobState } from '@/lib/aiJobSocket';

interface GenerateAttractionTranslationsParams {
  attractionId: string;
  targetLanguageIds: string[];
}

interface DispatchResponse {
  success: boolean;
  data?: AiJobState;
  error?: string;
}

/**
 * Hook for generating attraction translations to other languages. The
 * underlying endpoint dispatches an Inngest job and returns 202 immediately;
 * this hook hands the resulting job off to the global notification provider
 * so the user gets a toast when the job actually finishes.
 */
export function useGenerateAttractionTranslations() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attractionId,
      targetLanguageIds,
    }: GenerateAttractionTranslationsParams): Promise<DispatchResponse> => {
      return apiClient.post<DispatchResponse>(
        `/attractions/${attractionId}/generate-translations`,
        { targetLanguageIds },
      );
    },
    onSuccess: (response) => {
      if (response.data?.jobId) {
        handoffAiJobToBackground(response.data.jobId);
      }
      queryClient.invalidateQueries({ queryKey: ['Translation'] });
      toast.success("Translations started — we'll notify you when they're ready.");
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start translations');
    },
  });
}
